// src/lib/planParse.ts
//
// Plan-from-paper: types + pure mapping between a parsed page's action items
// and addTask arguments. Kept out of the components so the placement rules
// (the part that writes data) are unit-testable without a DOM.

import type { TaskBucket, TaskContext } from '@/types/task'
import type { PageNote } from '@/lib/pageParse'
import type { DomainId } from '@/lib/domains'
import { localYmd, parseLocalYmd } from '@/lib/cadence/config'
import { seasonStartFor, seasonEndFor, nextSeasonStart, readSeasons, normalizeSeasons, type Seasons } from '@/lib/cadence/seasons'

/** How far ahead the parser may place: today + 13 days covers "the week I just
 *  planned" from any day of the week (a Sunday page maps Mon–Sun of next week). */
export const PLAN_WINDOW_DAYS = 14

/** Which page was photographed. The altitude sizes the placement window and
 *  says where an undated line lands. 'week' is the daily scratchpad / week
 *  plan — the only altitude before 2026-09-05. Mirrors parse-page's own. */
export type PageAltitude = 'week' | 'month' | 'season' | 'year'

export const PAGE_ALTITUDES: { id: PageAltitude; label: string; hint: string }[] = [
  { id: 'week', label: 'Week', hint: 'A day or the week ahead' },
  { id: 'month', label: 'Month', hint: 'The month ahead' },
  { id: 'season', label: 'Season', hint: 'The next three months' },
  { id: 'year', label: 'Year', hint: 'Goals for the year' },
]

export function isPageAltitude(v: unknown): v is PageAltitude {
  return v === 'week' || v === 'month' || v === 'season' || v === 'year'
}

export type PlanPlacement =
  | { kind: 'date'; date: string } // local YYYY-MM-DD
  | { kind: 'week' }
  | { kind: 'month' }
  | { kind: 'season' } // bucket 'quarter'
  | { kind: 'someday' }
  | { kind: 'inbox' }
  | { kind: 'goal' } // a `goals` row, year pages only — not a task

/** Where a line lands when it names no day, and where an out-of-window date
 *  degrades to: the page's own altitude. */
export function defaultPlacement(altitude: PageAltitude): PlanPlacement {
  return altitude === 'year' ? { kind: 'goal' } : { kind: altitude }
}

const HORIZON_KINDS = new Set(['week', 'month', 'season', 'someday', 'inbox'])

export type PlanDay = 'sun' | 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat'
const PLAN_DAYS: readonly PlanDay[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']
export interface PlanRecurring { days: PlanDay[]; until: string | null }

export interface PlanItem {
  title: string
  placement: PlanPlacement
  /** A goal line on a MONTH or SEASON page: written as `is_goal` on that
   *  page's list (a goal is what the period is for; it is ticked, never
   *  placed). Distinct from the 'goal' placement, which is a year `goals` row. */
  goal?: boolean
  /** Local clock time as "HH:MM" (24h) when the line named one, else null.
   *  Only meaningful alongside a 'date' placement — a time with no day has
   *  nothing to hang on. A page that writes "Dentist 2pm" means a 2pm block,
   *  not an all-day chip with "2pm" buried in the note. */
  time: string | null
  assigneeId: string | null
  note: string | null
  /** The raw YYYY-MM-DD the line named, kept even when the row was degraded
   *  out of the window — so a chip flip can put the date back (spec C2). */
  dateHint: string | null
  /** A day-fact ("no school") is not a to-do; a recurring line is a routine. */
  kind: 'task' | 'dayfact' | 'recurring'
  /** For a 'task' row: what kind of item it is saved as — Task, Appointment
   *  ('event') or Activity (`tasks.category`). Chosen on the review sheet;
   *  absent = a plain task. Ignored on day-facts and routines. */
  category?: 'task' | 'event' | 'activity'
  recurring: PlanRecurring | null
  phone: string | null
  /** The named member a line is ABOUT (e.g. "Mia: dentist" → Mia), set by
   *  decideAssignment in validatePageResult — never the doer. Null when the
   *  line names no one, or when a named adult IS the doer (assigneeId instead). */
  contactMemberId: string | null
  /** Lineage chosen on the sheet: the existing task this row is a copy of. */
  sourceId?: string
}

const HHMM = /^([01]\d|2[0-3]):([0-5]\d)$/

/** Apply "HH:MM" to a local date. Returns a copy; invalid times are ignored. */
export function applyTimeToDate(date: Date, time: string | null): Date {
  if (!time || !HHMM.test(time)) return date
  const [h, m] = time.split(':').map(Number)
  const out = new Date(date)
  out.setHours(h, m, 0, 0)
  return out
}

const YMD = /^\d{4}-\d{2}-\d{2}$/

/** A season page looks three months out. */
/** The 1st of the month a MONTH page is for: the current month, unless the
 *  page is snapped in its last 7 days — a page written on the 28th is for
 *  October. */
export function pageMonthStart(today: Date): Date {
  const d = new Date(today)
  d.setHours(0, 0, 0, 0)
  const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()
  const forNext = lastDay - d.getDate() < 7
  return new Date(d.getFullYear(), d.getMonth() + (forNext ? 1 : 0), 1)
}

/** The start of the season a SEASON page is for, per the household
 *  boundaries: the current season, unless the page is snapped in its last 14
 *  days, when it is for the coming one. */
export function pageSeasonStart(today: Date, seasons: Seasons = readSeasons()): Date {
  const d = new Date(today)
  d.setHours(0, 0, 0, 0)
  const end = seasonEndFor(d, seasons)
  const daysLeft = Math.round((end.getTime() - d.getTime()) / 86_400_000)
  return daysLeft <= 14 ? nextSeasonStart(d, seasons) : seasonStartFor(d, seasons)
}

/** The dates a page may place on, today first: a week page 14 days; a month
 *  page today through the END of next month (so a page written on the 28th
 *  can place into the coming month); a season page today through the end of
 *  the season the page is for (pageSeasonStart, from the household
 *  boundaries); a year page none. */
export function planWindowDates(today: Date, altitude: PageAltitude = 'week', seasons: Seasons = readSeasons(), periodStart?: Date): string[] {
  if (altitude === 'year') return []
  const cursor = new Date(today)
  cursor.setHours(0, 0, 0, 0)
  const end = new Date(cursor)
  if (altitude === 'month') {
    // Through the end of the month the page is FOR (default: next month's end).
    const target = periodStart ?? new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1)
    end.setTime(new Date(target.getFullYear(), target.getMonth() + 1, 0).getTime())
  } else if (altitude === 'season') {
    // seasonStartFor/seasonEndFor assume a calendar-ordered array — normalize
    // so a caller's own (possibly hand-written, out-of-month-order) boundary
    // list doesn't wrap into the wrong year.
    const normSeasons = normalizeSeasons(seasons)
    // seasonEndFor is exclusive; the window's last day is the day before it.
    const start = periodStart ?? pageSeasonStart(cursor, normSeasons)
    const exclusiveEnd = seasonEndFor(start, normSeasons)
    end.setTime(exclusiveEnd.getTime())
    end.setDate(end.getDate() - 1)
  } else {
    end.setDate(end.getDate() + PLAN_WINDOW_DAYS - 1)
  }
  const out: string[] = []
  while (cursor <= end) {
    out.push(localYmd(cursor))
    cursor.setDate(cursor.getDate() + 1)
  }
  return out
}

/** Re-place items against a new window without a second model call (spec C2). */
export function rewindowPlanItems(items: PlanItem[], windowDates: string[], altitude: PageAltitude): PlanItem[] {
  const win = new Set(windowDates)
  return items.map((item) => {
    if (item.placement.kind === 'goal' || item.placement.kind === 'inbox' || item.placement.kind === 'someday') return item
    if (item.dateHint && win.has(item.dateHint)) {
      return { ...item, placement: { kind: 'date', date: item.dateHint } }
    }
    if (item.placement.kind === 'date' && !win.has(item.placement.date)) {
      return { ...item, placement: defaultPlacement(altitude), time: null }
    }
    return item
  })
}

/** A ★, an underline, a circle on the page is emphasis, not content. */
export const EMPHASIS_NOTE = /^(starred|star|priority|important|underlined|circled|highlighted)\.?$/i
export function isEmphasisNote(note: string | null): boolean {
  return !!note && EMPHASIS_NOTE.test(note.trim())
}

const PHONE = /(\+?1[\s.-]?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}/

function cleanNote(title: string, note: unknown): string | null {
  if (typeof note !== 'string') return null
  const n = note.trim()
  if (!n || isEmphasisNote(n)) return null
  // "both kids" under "Book dentist checkups for both kids" says nothing new.
  if (title.toLowerCase().includes(n.toLowerCase())) return null
  return n
}

function readRecurring(raw: unknown): PlanRecurring | null {
  const r = raw as { days?: unknown; until?: unknown } | null
  if (!r || typeof r !== 'object') return null
  const days = Array.isArray(r.days) ? r.days.filter((d): d is PlanDay => typeof d === 'string' && (PLAN_DAYS as readonly string[]).includes(d)) : []
  const until = typeof r.until === 'string' && YMD.test(r.until) ? r.until : null
  return { days, until }
}

/**
 * Validate the edge function's items into PlanItems. The function already
 * validates server-side; this repeats the cheap parts so a stale/hand-rolled
 * response can't write a placement outside the window the user was shown.
 */
export function validatePlanItems(
  raw: unknown,
  windowDates: string[],
  memberIds: Set<string>,
  altitude: PageAltitude = 'week',
): PlanItem[] {
  const items = (raw as { items?: unknown } | null)?.items
  if (!Array.isArray(items)) return []
  const window = new Set(windowDates)
  const out: PlanItem[] = []
  for (const entry of items) {
    const e = entry as { title?: unknown; day?: unknown; time?: unknown; assignee_id?: unknown; note?: unknown; date_hint?: unknown; kind?: unknown; recurring?: unknown; phone?: unknown }
    if (typeof e.title !== 'string' || !e.title.trim()) continue
    const day = typeof e.day === 'string' ? e.day : 'inbox'
    // A goal line: a year `goals` row on a year page; a goal ON THE LIST of a
    // month or season page; a week page has no goals, so it becomes a wish.
    const goalOnPage = day === 'goal' && (altitude === 'month' || altitude === 'season')
    const placement: PlanPlacement =
      day === 'goal' ? (altitude === 'year' ? { kind: 'goal' } : goalOnPage ? defaultPlacement(altitude) : { kind: 'someday' })
      : HORIZON_KINDS.has(day) ? { kind: day as 'week' | 'month' | 'season' | 'someday' | 'inbox' }
      : YMD.test(day) && window.has(day) ? { kind: 'date', date: day }
      : defaultPlacement(altitude)
    let kind: PlanItem['kind'] = e.kind === 'dayfact' || e.kind === 'recurring' ? e.kind : 'task'
    let recurring = kind === 'recurring' ? (readRecurring(e.recurring) ?? { days: [], until: null }) : null
    // Force goals to be tasks, never routines
    if (placement.kind === 'goal' || goalOnPage) {
      kind = 'task'
      recurring = null
    }
    const title = e.title.trim()
    out.push({
      title,
      placement,
      ...(goalOnPage ? { goal: true } : {}),
      // A time survives on a real date, or on a recurring line (it becomes the routine's time).
      time: (placement.kind === 'date' || kind === 'recurring') && typeof e.time === 'string' && HHMM.test(e.time.trim())
        ? e.time.trim()
        : null,
      assigneeId: typeof e.assignee_id === 'string' && memberIds.has(e.assignee_id) ? e.assignee_id : null,
      note: cleanNote(title, e.note),
      dateHint: typeof e.date_hint === 'string' && YMD.test(e.date_hint) ? e.date_hint : (YMD.test(day) ? day : null),
      kind,
      recurring,
      phone: typeof e.phone === 'string' && PHONE.test(e.phone) ? e.phone.trim() : null,
      // Overwritten by decideAssignment in validatePageResult, which has the
      // household roles this pass doesn't.
      contactMemberId: null,
    })
  }
  return out
}

export interface PlanCommitContext {
  /** Start of the current week (weekStartAnchor) — stamps bucket='week' rows. */
  currentWeekStart: Date
  /** The month a month row is for (its 1st) — stamps bucket='month' rows. */
  monthStart: Date
  /** The season a season row is for (its start) — stamps bucket='quarter' rows. */
  seasonStart: Date
  /** The page's domain — a committed page always writes a real life area now
   *  (Task 5); there is no more "Universal"/untagged commit. */
  context: TaskContext
}

export interface PlanAddTaskArgs {
  title: string
  scheduledFor: Date | undefined
  /** addTask's SECOND positional arg, which lands in `tasks.contact_id`.
   *
   *  Always `undefined` from a page. `tasks.contact_id` has a foreign key to
   *  `contacts`, and the member a line is ABOUT is a `family_members` row —
   *  writing that id raised FK 23503, addTask returned undefined, and every
   *  line about a kid came back as "N items could not be saved". The member
   *  still rides the PlanItem as `contactMemberId` (the review sheet draws its
   *  avatar from it) and the kid's name is already in the title. */
  contactId: string | undefined
  options: {
    bucket?: TaskBucket
    weekStart?: Date
    monthStart?: Date
    seasonStart?: Date
    isGoal?: boolean
    pickedAt?: Date
    isAllDay?: boolean
    /** Who the review sheet settled on: a member id, or null for Unassigned.
     *  Always explicit — never left for addTask to default. */
    assignedTo: string | null
    context: TaskContext | null
    /** Task / Appointment ('event') / Activity, as chosen on the sheet. */
    category?: 'task' | 'event' | 'activity'
    notes?: string
    /** Tap-to-call number a line named ("Call the vet, 410-555-0142"). */
    phoneNumber?: string
    /** Lineage: the existing task this row is a copy of. */
    sourceId?: string
  }
}

/**
 * One item → the arguments for ONE addTask INSERT. Everything rides the insert
 * (bucket, weekStart, notes) — a follow-up update can be silently dropped
 * before the temp→real id swap lands (the addTask-then-setBucket race).
 */
export function planItemToAddTaskArgs(item: PlanItem, ctx: PlanCommitContext): PlanAddTaskArgs {
  const base = {
    // The sheet decided every row's person (the signed-in member by default,
    // Unassigned when chosen): null is written as null, not re-defaulted.
    assignedTo: item.assigneeId,
    context: ctx.context,
    ...(item.category ? { category: item.category } : {}),
    notes: item.note ?? undefined,
    phoneNumber: item.phone ?? undefined,
    sourceId: item.sourceId,
  }
  // NEVER item.contactMemberId: that is a `family_members` id and
  // `tasks.contact_id` points at `contacts` (FK 23503 → the whole page fails).
  const contactId: string | undefined = undefined
  switch (item.placement.kind) {
    case 'date': {
      // A named clock time makes this a real block on the day, not an all-day
      // chip. Times written on paper ("Dentist 2pm") used to land in the note
      // and the task arrived all-day — the placement the user drew on the page
      // was silently discarded.
      const day = parseLocalYmd(item.placement.date)
      return {
        title: item.title,
        scheduledFor: applyTimeToDate(day, item.time),
        contactId,
        options: { ...base, isAllDay: !item.time },
      }
    }
    case 'week':
      // bucket='week' rows must say WHICH week (placement cascade) — an
      // unstamped row reads as "the current week" only by legacy accident.
      return {
        title: item.title,
        scheduledFor: undefined,
        contactId,
        options: { ...base, bucket: 'week', weekStart: ctx.currentWeekStart },
      }
    case 'month':
      // Stamped with the month the PAGE is for, and is_goal when the line was
      // a goal — both ride the INSERT (the addTask-then-update race).
      return { title: item.title, scheduledFor: undefined, contactId, options: { ...base, bucket: 'month', monthStart: ctx.monthStart, isGoal: !!item.goal } }
    case 'season':
      // Written on the season page = picked for the season. The pick mark
      // rides the INSERT like every other field here.
      return { title: item.title, scheduledFor: undefined, contactId, options: { ...base, bucket: 'quarter', seasonStart: ctx.seasonStart, isGoal: !!item.goal, pickedAt: new Date() } }
    case 'someday':
      return { title: item.title, scheduledFor: undefined, contactId, options: { ...base, bucket: 'someday' } }
    case 'goal':
      // A goal is a `goals` row, written by useCommitPage — it never reaches
      // this mapper on the happy path. If one does, Someday keeps it visible
      // instead of losing a line the user wrote down.
      return { title: item.title, scheduledFor: undefined, contactId, options: { ...base, bucket: 'someday' } }
    case 'inbox':
      return {
        title: item.title,
        scheduledFor: undefined,
        contactId,
        options: { ...base, bucket: 'inbox' },
      }
  }
}

/**
 * What the review sheet hands on: the checked rows, as edited, plus the one
 * domain the page belongs to and the period its chip settled on. Lives here,
 * beside `PlanItem`, so the pure modules that consume a reviewed page
 * (paperIntoDraft) need not reach into a component for a type.
 */
export interface PageReviewPayload {
  items: PlanItem[]
  notes: PageNote[]
  /** Which layer the whole page belongs to — asked once, on the sheet, and
   *  stamped on everything the page writes. Family is the sharing switch. */
  domain: DomainId
  /** The month a MONTH page is for (its 1st) — the chip's choice. */
  monthStart?: Date
  /** The season a SEASON page is for (its start) — the chip's choice. */
  seasonStart?: Date
}
